import * as React from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StraightenIcon from "@mui/icons-material/Straighten";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import NoteIcon from "@mui/icons-material/Note";
import BuildIcon from "@mui/icons-material/Build";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ScienceIcon from "@mui/icons-material/Science";
import { useNavigate } from "react-router-dom";
import { Template } from "../../types/template";
import MapPreview from "../../components/MapPreview";
import { nl2br } from "../../utils/helper";
import { decode } from "html-entities";
import { GlassDrawer } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const options = {
  fullscreenControl: false, // remove the top-right button
  mapTypeControl: false, // remove the top-left buttons
  streetViewControl: false, // remove the pegman
  zoomControl: false, // remove the bottom-right buttons
  scrollwheel: false,
};

interface ProjectTemplateDrawerProps {
  data: Template;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function ProjectTemplateDrawer(
  props: ProjectTemplateDrawerProps
) {
  const { data, open, setOpen } = props;

  const navigate = useNavigate();
  const theme = useTheme();
  const locationAddressLabel = useUILabel("common.locationAddress");
  const clientAddressLabel = useUILabel("common.clientAddress");

  const [lng, lat] = React.useMemo(() => {
    if (!!data?.customer) {
      return data?.customer?.coordinate?.coordinates || [];
    } else if (!!data?.location) {
      return data?.location?.coordinate?.coordinates || [];
    } else return [];
  }, [data]);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      setOpen(open);
    };

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 460 },
        },
      }}
      contentProps={{ role: "presentation" }}
    >
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              justifyContent: "flex-end",
              mt: { xs: 0.5, sm: 1.5 },
            }}
          >
        <ClearIcon
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
              sx={{ cursor: "pointer" }}
        />
          </Box>
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: theme.shadows[2],
              width: "100%",
              minHeight: { xs: 200, sm: 240 },
              "& > div": {
                borderRadius: 2,
              },
            }}
          >
      <MapPreview lat={lat} lng={lng} />
          </Box>
        <List
          sx={{
            "& .MuiDivider-root": {
              my: 1,
            },
          }}
        >
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="ID/Title"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <AssignmentIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {`#${data?.id} ${data?.title}`}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Scope of Work"
            secondary={
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
                <DescriptionIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0, mt: 0.25 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }} component="div">
                  {nl2br(decode(data?.description))}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        {!!data?.location && (
          <>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={locationAddressLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.location.address_1}, ${data?.location.city}, ${data?.location.zip}`}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}
        {!!data?.customer && (
          <>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={clientAddressLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.customer?.physical_address_1}, ${data?.customer?.city}, ${data?.customer?.zip}`}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider />
          </>
        )}
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Estimated Amount of Hours"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {`${data?.approximate_hours_to_complete} hr`}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Mile Radius"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <StraightenIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {`${data?.mile_radius} miles`}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Photo(s) Required"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <CameraAltIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {data?.is_photo_required ? "Yes" : "No"}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Final Notes Required"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <NoteIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {data?.is_final_notes_required ? "Yes" : "No"}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Tool(s) Required"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <BuildIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {data?.tool_required ? "Yes" : "No"}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Background Check Required"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <VerifiedUserIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {data?.background_check_required ? "Yes" : "No"}
                </Typography>
              </Box>
            }
          />
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText
            primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
            primary="Drug Test Required"
            secondary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <ScienceIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                  {data?.drug_test_required ? "Yes" : "No"}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      </List>
        <Box display="flex" justifyContent="center" sx={{ mt: "auto" }}>
        <Button
            fullWidth
          variant="contained"
          onClick={() => navigate(`/works/templates/${data?.id}`)}
        >
          More Detail
        </Button>
      </Box>
    </GlassDrawer>
  );
}
